import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { Card, CardContent, CardTitle } from '@xxtract/ui';
import { hasPeriodOrStatusFilter, type ProcessFilterValues } from '../procesLijsten';
import type { PhysicalSummary, PhysicalTargetStep } from '../processes.api';

/** Geen zichtbare tekst maar het pad naar de vertaling — vandaar als constante buiten de render. */
const STEP_TRANSLATION_PREFIX = 'processes.physical.steps.';

/**
 * De fysieke tegel uit `mockup-processen-portaal.html`: één brede kaart met genummerde bollen per
 * ketenstap en de telling eronder. Bewust anders dan een dienstkaart — fysiek werk is een keten die
 * dwars door de diensten loopt, geen dienst naast de andere.
 *
 * spec-processen-actuele-stand: de cijfers tellen alleen producten op lopende orders, elk in de stap
 * waar het nu staat. Is die set niet te bepalen (`eventsUnavailable`), dan toont de tegel geen getallen
 * maar "bron niet bereikbaar" — nooit een terugval op historie.
 */
export function FysiekeKetenTegel({
  process,
  chain,
  eventsUnavailable,
  filters = {},
}: {
  process: PhysicalSummary;
  chain: PhysicalTargetStep[];
  eventsUnavailable: boolean;
  /** De Processen-filter van de pagina (story 12): de productenlijst leest dezelfde gedeelde stand. */
  filters?: ProcessFilterValues;
}) {
  const { t } = useTranslation();

  return (
    <Link
      className="group block rounded-[var(--radius-card)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--secondary)]"
      data-testid="processes-physical-tile-link"
      to="/processes/in-huis"
    >
      <Card className="border-[var(--secondary-light)] bg-[var(--background)] transition-shadow group-hover:border-[var(--secondary)] group-hover:shadow-md" data-testid="processes-physical-tile">
        <CardContent className="space-y-4 pt-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <CardTitle className="text-sm">{t('processes.names.physical')}</CardTitle>
              <p className="text-[10px] font-semibold uppercase tracking-wide text-[var(--foreground-muted)]">PHYSICAL</p>
            </div>
            {eventsUnavailable ? (
              <p className="text-xs font-semibold text-[var(--info-text)]" data-testid="processes-physical-unavailable" role="status">{t('processes.degraded.title')}</p>
            ) : (
              <div className="flex items-baseline gap-4">
                <div>
                  <span className="text-2xl font-bold tracking-tight text-[var(--foreground)]" data-testid="processes-physical-open">
                    {process.openProductCount}
                  </span>
                  {/* De spatie staat er echt, niet alleen als marge: anders leest een schermlezer "1072open producten". */}
                  <span className="ml-1.5 text-xs text-[var(--foreground-muted)]"> {t('processes.card.openProductsLabel')}</span>
                </div>
                <div>
                  <span className={`text-2xl font-bold tracking-tight ${process.stagnation.count > 0 ? 'text-[var(--destructive)]' : 'text-[var(--foreground-muted)]'}`} data-testid="processes-physical-stagnating">
                    {process.stagnation.count}
                  </span>
                  <span className="ml-1.5 text-xs text-[var(--foreground-muted)]"> {t('processes.card.stagnationLabel')}</span>
                </div>
              </div>
            )}
          </div>
          {/* Story 4: the counts follow customer and threshold, not period or status. */}
          {hasPeriodOrStatusFilter(filters) ? (
            <p className="text-[11px] text-[var(--info-text)]" data-testid="processes-physical-period-status">{t('processes.allCustomers.physicalCounts')}</p>
          ) : null}

          {/* CAP-8: een afgekapte orderophaal maakt ook de fysieke set te klein. */}
          {process.ordersTruncated ? (
            <p className="text-[11px] text-[var(--info-text)]" data-testid="processes-physical-orders-truncated">{t('processes.ordersTruncated.fysiek')}</p>
          ) : null}
          {(process.withoutCustomerKey ?? 0) > 0 || (process.unlinkedCount ?? 0) > 0 ? (
            <ul className="space-y-0.5 text-[11px] text-[var(--foreground-muted)]">
              {(process.withoutCustomerKey ?? 0) > 0 ? (
                <li data-testid="processes-physical-without-customer-key">{t('processes.physical.withoutCustomerKey', { count: process.withoutCustomerKey })}</li>
              ) : null}
              {(process.unlinkedCount ?? 0) > 0 ? <li data-testid="processes-physical-unlinked">{t('processes.physical.unlinked', { count: process.unlinkedCount })}</li> : null}
            </ul>
          ) : null}

          <ol className="flex flex-wrap gap-2">
            {chain.map((step, index) => {
              const tracked = step.count != null;
              const offline = step.coverageState === 'publisher_offline';
              // De stapnaam komt uit de vertaling van de ketensleutel; kent de vertaling hem niet
              // (een stap buiten de keten), dan blijft de ruwe code weg en tonen we de sleutel.
              const stepKey = `${STEP_TRANSLATION_PREFIX}${step.key}`;
              return (
                <li className="flex min-w-[92px] flex-1 flex-col items-center gap-1" data-testid={`processes-physical-step-${step.key}`} key={step.key}>
                  <span
                    aria-hidden="true"
                    className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold ${
                      tracked ? 'bg-[var(--secondary)] text-white' : 'border-2 border-[var(--border)] bg-[var(--background)] text-[var(--foreground-muted)]'
                    }`}
                  >
                    {index + 1}
                  </span>
                  <span className="text-[11px] text-[var(--foreground-muted)]">{t(stepKey, { defaultValue: step.key })}</span>
                  <span className="text-xs font-semibold text-[var(--foreground)]">{tracked ? step.count : offline ? t('processes.physical.stepUnavailable') : t('processes.physical.stepNotTracked')}</span>
                </li>
              );
            })}
          </ol>

          <p className="text-right text-[11px] font-semibold text-[var(--secondary)]">{t('processes.physical.viewItems')}</p>
        </CardContent>
      </Card>
    </Link>
  );
}
